import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Order } from '../../../models/order/orders';
import { getMerchantName, getProductImageUrl } from '../../../models/product/product';
import { ROUTES } from '../../../constants/routes';

const OrderSuccess: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';

  const order = location.state?.order as Order;

  // If no order data, redirect to home
  React.useEffect(() => {
    if (!order) {
      navigate(ROUTES.HOME);
    }
  }, [order, navigate]);

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('order.success.title', 'Order Confirmed!')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('order.success.subtitle', 'Thank you for your order. We\'ll send you a confirmation email shortly.')}
          </p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
          {/* Header with Order Number */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('order.success.order-number', 'Order #{{orderNumber}}', { orderNumber: order.orderNumber })}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('order.success.placed-on', 'Placed on')} {new Date(order.createdAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {t(`order-status.${order.status}`, order.status)}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Customer and Shipping Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t('order.success.shipping-address', 'Shipping Address')}
                </h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p className="font-medium text-gray-900">
                    {order.customerInfo.firstName} {order.customerInfo.lastName}
                  </p>
                  <p>{order.shippingAddress.street}</p>
                  <p>{order.shippingAddress.city}, {order.shippingAddress.state}</p>
                  <p>{order.shippingAddress.country} {order.shippingAddress.postalCode}</p>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p>{order.customerInfo.email}</p>
                    <p>{order.customerInfo.phone}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t('order.success.delivery-info', 'Delivery Information')}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <div>
                      <p className="font-medium text-gray-900">
                        {t('checkout.payment.cod', 'Cash on Delivery (COD)')}
                      </p>
                      <p className="text-sm text-gray-600">
                        {t('order.success.cod-description', 'Pay when you receive your order')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-medium text-gray-900">
                        {t('order.success.estimated-delivery', 'Estimated Delivery')}
                      </p>
                      <p className="text-sm text-gray-600">
                        {order.estimatedDelivery}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('order.success.order-items', 'Order Items')}
              </h3>
              <div className="divide-y divide-gray-200">
                {order.items.map((item) => (
                  <div key={`${item.product.id}-${item.product.type}`} className="py-4">
                    <div className="flex items-center space-x-4">
                      <img
                        src={getProductImageUrl(item.product)}
                        alt={item.product.name}
                        className="w-16 h-20 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-medium text-gray-900 truncate">
                          {item.product.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {t('products.books.by-author', 'by')} {getMerchantName(item.product)}
                        </p>
                        {/* <p className="text-sm text-gray-600">
                          {t(`books.format.${item.format}`, item.format)} • {t('checkout.quantity', 'Qty')}: {item.quantity}
                        </p> */}
                      </div>
                      <div className="text-right">
                        <p className="text-base font-medium text-gray-900">
                          {(item.product.price * item.quantity).toFixed(0)} EGP
                        </p>
                        {item.quantity > 1 && (
                          <p className="text-sm text-gray-600">
                            {item.product.price.toFixed(0)} EGP × {item.quantity}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="border-t border-gray-200 pt-6">
              <div className="max-w-sm ml-auto space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('checkout.subtotal', 'Subtotal')}:</span>
                  <span className="font-medium">{order.subtotal.toFixed(2)} {t('currency.egp', 'EGP')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('checkout.shipping', 'Shipping')}:</span>
                  <span className={`font-medium ${order.shipping === 0 ? 'text-green-600' : ''}`}>
                    {order.shipping === 0 
                      ? t('checkout.free', 'Free') 
                      : `${order.shipping.toFixed(2)} ${t('currency.egp', 'EGP')}`
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('checkout.tax', 'Tax (5%)')}:</span>
                  <span className="font-medium">{order.tax.toFixed(2)} {t('currency.egp', 'EGP')}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                  <span>{t('checkout.total', 'Total')}:</span>
                  <span>{order.total.toFixed(2)} {t('currency.egp', 'EGP')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate(ROUTES.STORE)}
            className="bg-black text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
          >
            {t('order.success.continue-shopping', 'Continue Shopping')}
          </button>
          <button
            onClick={() => window.print()}
            className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>{t('order.success.print-order', 'Print Order')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;